import { useTranslation } from '@/contexts/i18n-context';
import { Form } from '@/components/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import styles from './styles.module.css';
import { CreateMedicationParams } from '@/api/api.schemas';
import { requiredString } from '@/util/validators';
import { useGetMedications } from '@/api/get-medications';
import { MedicationIcon } from '../icons/medication-icon';
import { Expander } from '@/components/expander';
import { SearchableSelect } from '@/components/inputs/searchable-select';
import { MedicalHistoryItemList } from '../medical-history-item-list';

const medicationSchema = z.object({
  externalId: requiredString(),
});

type FormValues = z.infer<typeof medicationSchema>;

interface MedicationOption {
  nameWithRouteDoseForm: string;
  doseSpotId: number;
  value: string;
  label: string;
}

interface MedicationsFormProps {
  onRemove: (id: string) => void;
  onAdd: (medication: CreateMedicationParams) => void;
  medications: CreateMedicationParams[];
  isOpen?: boolean;
  onExpanderClick?: () => void;
}

const translationBase = 'visit.medicalHistory.medicationsForm';

export function MedicationsForm({
  onRemove,
  onAdd,
  medications,
  isOpen,
  onExpanderClick,
}: MedicationsFormProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const form = useForm<FormValues>({
    resolver: zodResolver(medicationSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues: {
      externalId: '',
    },
  });

  const enableSearch = searchTerm.trim().length > 1;

  const { data: selectMedications, isFetching } = useGetMedications(
    { searchTerm },
    { query: { enabled: enableSearch } },
  );

  const medicationsNames = medications.map((med) => med.text);
  const selectMedicationsToShow = selectMedications?.medications?.filter(
    (med) => !medicationsNames.includes(med.nameWithRouteDoseForm),
  );

  const options: MedicationOption[] =
    selectMedicationsToShow?.map((medication) => ({
      ...medication,
      value: medication.doseSpotId.toString(),
      label: medication.nameWithRouteDoseForm,
    })) ?? [];

  return (
    <Expander
      icon={<MedicationIcon className={styles.icon} />}
      title={t(`${translationBase}.title`)}
      indicatorText={t(`${translationBase}.indicatorText`)}
      collapsedText={medications.map((medication) => medication.text).join(', ')}
      isOpen={isOpen}
      onClick={onExpanderClick}
    >
      <Form form={form} className={styles.form}>
        <div className={styles.formContainer}>
          <SearchableSelect
            className={styles.input}
            label={t(`${translationBase}.medicationLabel`)}
            name="externalId"
            options={options}
            placeholder={t(`${translationBase}.medicationPlaceholder`)}
            onSearch={setSearchTerm}
            isLoading={isFetching}
            menuIsOpen={enableSearch}
            onChange={(value) => {
              onAdd({
                externalId: value.value,
                text: value.label,
              });
            }}
          />
        </div>
        <MedicalHistoryItemList
          items={medications.map((medication) => ({
            id: medication.externalId,
            text: medication.text,
          }))}
          onRemove={onRemove}
        />
      </Form>
    </Expander>
  );
}
